import React, { useState } from 'react';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Divider,
  Paper,
  Tooltip,
  IconButton
} from '@material-ui/core';
import {
  AuxInfo,
  BadgeSection,
  PriceSection,
  ViewContainersModal
} from 'components/underwrite/shared/header';
import { HeaderImage } from 'components/underwrite/postAcquisition/postAcqUnderwrite/header';
import { fullAddress } from 'utilities/address';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  setPostAcqWorkflowGridState, fetchPostAcqUWMsrHomes, setIsFetchingPostAcqUWMsrHomes
} from 'store/actions';
import { push } from 'connected-react-router';
import { Constants } from 'utilities/constants';
import HouseIcon from '@material-ui/icons/House';
import { MsrHomesModal } from 'components/underwrite/shared';
import AmherstIcon from "assets/images/rex/AmherstIcon.png";
import { useSaveUWDialog } from 'components/underwrite/shared/SaveUWDialog';
import WaveIcon from '@material-ui/icons/Waves';

const PostAcqUWHeader = ({ containerClass, classes, push, selectedOpportunity, marketData, setPostAcqWorkflowGridState,
  valuationIsDirty, fetchPostAcqUWMsrHomes, setIsFetchingPostAcqUWMsrHomes, isAmherstManaged, selectedContainer, viewingReadonlyMode
}) => {
  const [isMsrHomesModalOpen, setIsMsrHomesModalOpen] = useState(false);
  const { openSaveUWDialog } = useSaveUWDialog();

  if (!selectedOpportunity) return null;

  if (!selectedContainer) return null;

  const viewTaskOnWorkflow = id => {
    if (valuationIsDirty) {
      openSaveUWDialog({ 
        saveUWDialogParameters: { route: Constants.Routes.POSTACQ_WORKFLOW },
        callback: () => {
          setPostAcqWorkflowGridState({ filterValue: [['id', '=', id]] }, true);
        }
      });
    } else {
      setPostAcqWorkflowGridState({ filterValue: [['id', '=', id]] }, true); // Filter grid by selectedOpportunity id
      push(Constants.Routes.POSTACQ_WORKFLOW);
    }
  }

  return (
    <Paper className={classNames(containerClass, classes.root)} elevation={0}>
      <div className={classes.mainInfoContainer}>
        <HeaderImage
          viewTaskOnWorkflow={viewTaskOnWorkflow}
          viewingReadonlyMode={viewingReadonlyMode}
        />
        <div className={classNames(classes.infoSection, classes.addressSection)} style={{ padding: 0 }}>
          <div className={classes.addressSectionRow}>
            {isAmherstManaged &&
              <div style={{ display: 'flex', paddingBotton: '3px' }}>
                <Tooltip title={"Amherst managed property"} placement="top">
                  <img src={AmherstIcon} alt="Amherst managed property" className={classes.amherstIcon} />
                </Tooltip>
                <Typography variant="h6" noWrap>{fullAddress(selectedOpportunity)}</Typography>
              </div>
            }
            {!isAmherstManaged && <Typography variant="h6" noWrap>{fullAddress(selectedOpportunity)}</Typography>}
          </div>
          <div className={classNames(classes.addressSectionRow, viewingReadonlyMode && classes.grayBackground)}>
            <BadgeSection selectedOpportunity={selectedOpportunity} />
          </div>
        </div>
        <div className={classNames(classes.vertDividerContainer, viewingReadonlyMode && classes.grayBackground)}>
          <Divider className={classes.vertDivider} />
        </div>
        <div className={classes.infoOverflow}>
          <div className={classNames(classes.infoSection, classes.priceSection, viewingReadonlyMode && classes.grayBackground)}>
            <div className={classes.textStrip}>
              <PriceSection module={Constants.Module.POSTACQ} selectedContainer={selectedContainer}/>
            </div>
            <div className={classes.textStrip}>
              <AuxInfo
                amherstEstimatedAMI={marketData?.amherstEstimatedAMI}
                schoolScoreAverage={selectedContainer?.schoolScoreAverage}
                schoolDistrict={selectedContainer?.schoolDistrict}
                selectedContainer={selectedContainer}
              />
            </div>
          </div>
          <div className={classNames(classes.vertDividerContainer, viewingReadonlyMode && classes.grayBackground)}>
            <Divider className={classes.vertDivider} />
          </div>
          <div className={classes.buttonWrapper}>
            <div className={classes.wrapper}>
              <Tooltip title="View MSR Homes" placement="bottom">
                <span>
                  <IconButton className={classes.headerButton} aria-label="View MSR Homes" disabled={viewingReadonlyMode} onClick={() => {
                    setIsFetchingPostAcqUWMsrHomes(true);
                    fetchPostAcqUWMsrHomes(selectedOpportunity.id);
                    setIsMsrHomesModalOpen(true);
                  }}>
                    <HouseIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
          <div className={classes.buttonWrapper}>
            <div className={classes.wrapper}>
              <Tooltip title='FEMA Map' placement="bottom">
                <IconButton className={classes.headerButton} aria-label="FEMA Map"
                  onClick={() => { 
                    if (!selectedOpportunity.longitude || !selectedOpportunity.latitude) {
                      return false;
                    }
                    window.open(`https://msc.fema.gov/portal/search?AddressQuery=${selectedOpportunity.longitude},${selectedOpportunity.latitude}`, '_blank');                 
                }}>
                  <WaveIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
        <ViewContainersModal module={Constants.Module.POSTACQ} txnOppId={selectedOpportunity.id}/>
        <MsrHomesModal
          module={Constants.Module.POSTACQ}
          isOpen={isMsrHomesModalOpen}
          setIsOpen={setIsMsrHomesModalOpen}
        />
      </div>
    </Paper>
  )
};

const controlWidth = 44;
const styles = theme => ({
  root: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 0,
    borderBottom: '1px solid #ddd'
  },
  vertDividerContainer: {
    padding: `${theme.spacing(1.5)}px 0`
  },
  vertDivider: {
    width: 1,
    height: '100%'
  },
  mainInfoContainer: {
    display: 'flex',
    height: '100%',
    overflowX: 'auto',
    overflowY: 'hidden'
  },
  infoSection: {
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    alignItems: 'flex-start',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  infoOverflow: {
    flexGrow: 1,
    display: 'flex', // B-added,
    paddingRight: '10px',
  },
  addressSection: {
    minWidth: 440
  },
  priceSection: {
  },
  textStrip: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    height: 30
  },
  headerButton: {
    padding: 4,
    margin: 4
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  wrapper: {
    position: 'relative',
  },
  amherstIcon: {
    height: '25px',
    width: '25px',
    marginRight: '5px',
    marginTop: '3px',
    borderRadius: `20%`,
    boxShadow: `0px 0px 5px 0px rgba(255, 255, 255, 0.6)`
  },
  addressSectionRow: {
    padding: '0 16px',
    height: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  grayBackground: {
    filter: 'grayscale(0.8) brightness(0.85) opacity(0.9)',
    backgroundColor: '#fff'
  }
});

const mapStateToProps = (state, ownProps) => {
  const uwState = ownProps.viewingReadonlyMode ? state.postAcqUWState.underwriteModalState : state.postAcqUWState;

  return {
    selectedOpportunity: uwState.postAcqUWClientTask,
    selectedContainer: uwState.selectedContainer,
    valuationIsDirty: state.postAcqUWState.valuationIsDirty,
    isAmherstManaged: uwState.selectedOpportunityAmherstIndicative?.isAmherstManaged,
    marketData: uwState.marketData
  }
};

const mapDispatchToProps = {
  push,
  setPostAcqWorkflowGridState,
  fetchPostAcqUWMsrHomes,
  setIsFetchingPostAcqUWMsrHomes
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(PostAcqUWHeader);
